import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useClassNames from '@medflex/hooks/useClassNames';
import isSupport from '@medflex/utils/isSupportPatient';
import SwitchButton, { Switch } from '../SwitchButton';
import ContactSearchField from '../ContactSearchField';
import SessionsNewAnonymousParticipantForm from './SessionsNewAnonymousParticipantForm';

const SessionsAddParticipantForm = ({
  className,
  contacts,
  onSubmit,
  maxParticipants,
  invitees,
  onDeleteParticipant,
  contactsOnly,
  defaultParticipant,
  canSendSms,
  smsUpgradeUrl,
}) => {
  const [type, setType] = useState('contacts');
  const { t } = useTranslation('components');

  const cn = useClassNames(
    () => ['flex', 'flex-col', 'bg-slate-100', 'rounded', 'p-2', 'lg:p-4', className],
    [className],
  );

  useEffect(() => {
    if (defaultParticipant && defaultParticipant.referenceType)
      setType(defaultParticipant.referenceType);
    else if (type === 'contacts') {
      const hasContacts = contacts.some(({ userId }) => !isSupport(userId));
      setType(hasContacts ? 'contacts' : 'email');
    }
  }, [contacts, defaultParticipant, type]);

  const handleTypeSwitch = ({ value }) => {
    setType(value);
    if (maxParticipants === 2) onDeleteParticipant();
  };

  const handleNewAnonymousParticipant = data => {
    onSubmit(data);
  };

  const handleContactAdded = data => {
    onSubmit(data);
  };

  return (
    <div className={cn} data-component="SessionsAddParticipantForm">
      {!contactsOnly && (
        <Switch onChange={handleTypeSwitch} className="mb-4">
          <SwitchButton value="contacts" checked={type === 'contacts'}>
            {t('Sessions.SessionsAddParticipantForm.typeSwitch.contacts')}
          </SwitchButton>
          <SwitchButton value="email" checked={type === 'email'}>
            {t('Sessions.SessionsAddParticipantForm.typeSwitch.email')}
          </SwitchButton>
          <SwitchButton value="phone" checked={type === 'phone'}>
            {t('Sessions.SessionsAddParticipantForm.typeSwitch.phone')}
          </SwitchButton>
        </Switch>
      )}
      {type === 'contacts' ? (
        <ContactSearchField onSubmit={handleContactAdded} contacts={contacts} />
      ) : (
        <SessionsNewAnonymousParticipantForm
          maxParticipants={maxParticipants}
          onSubmit={handleNewAnonymousParticipant}
          onDeleteParticipant={onDeleteParticipant}
          referenceType={type}
          defaultParticipant={defaultParticipant}
          hideParticipantInvitationOption={type === 'phone' || (invitees && invitees.length >= 1)}
          canSendSms={canSendSms}
          smsUpgradeUrl={smsUpgradeUrl}
        />
      )}
    </div>
  );
};

SessionsAddParticipantForm.propTypes = {
  onSubmit: PropTypes.func,
  onDeleteParticipant: PropTypes.func,
  maxParticipants: PropTypes.number,
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string,
      fullNameWithTitle: PropTypes.string,
      avatar: PropTypes.string,
      initials: PropTypes.string,
    }),
  ),
  invitees: PropTypes.arrayOf(
    PropTypes.shape({
      reference: PropTypes.string,
      fullNameWithTitle: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.string,
      initials: PropTypes.string,
      participantId: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.oneOf(['moderator'])),
    }),
  ),
  defaultParticipant: PropTypes.shape({
    userId: PropTypes.string,
    fullNameWithTitle: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    avatar: PropTypes.string,
    referenceType: PropTypes.string,
    initials: PropTypes.string,
  }),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  contactsOnly: PropTypes.bool,
  canSendSms: PropTypes.bool.isRequired,
  smsUpgradeUrl: PropTypes.string,
};

SessionsAddParticipantForm.defaultProps = {
  onSubmit: () => {},
  maxParticipants: 9,
  contactsOnly: false,
  smsUpgradeUrl: '',
};

export default SessionsAddParticipantForm;
